import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, RefreshCw, Users, Briefcase, FileSpreadsheet, FileText } from "lucide-react";
import { getPayrollReport, generateReportPdf, type PayrollReport } from "@/utils/reports.functions";
import { downloadCsv, downloadPdfFromBase64 } from "@/lib/report-export";
import { useLocaleFormat } from "@/lib/i18n-format";
import { PdfPreviewDialog } from "@/components/reports/PdfPreviewDialog";

export const Route = createFileRoute("/_app/reports/payroll")({
  component: PayrollReportPage,
});

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoISO(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function PayrollReportPage() {
  const { t } = useTranslation();
  const { company } = useAuth();
  const { formatMoney, formatNumber } = useLocaleFormat();
  const currency = company?.currency ?? "USD";

  const [periodStart, setPeriodStart] = useState(daysAgoISO(30));
  const [periodEnd, setPeriodEnd] = useState(todayISO());
  const [userFilter, setUserFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [contractFilter, setContractFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [data, setData] = useState<PayrollReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"csv" | "pdf" | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewBase64, setPdfPreviewBase64] = useState<string | null>(null);
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState<string>("");
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [contracts, setContracts] = useState<
    Array<{ id: string; number: string; title: string; client_id: string | null }>
  >([]);
  const [projects, setProjects] = useState<
    Array<{
      id: string;
      name: string;
      code: string;
      client_id: string | null;
      contract_id: string | null;
    }>
  >([]);

  const reportFn = useServerFn(getPayrollReport);
  const pdfFn = useServerFn(generateReportPdf);

  // Load filter options
  useEffect(() => {
    if (!company?.id) return;
    void Promise.all([
      supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("full_name"),
      supabase
        .from("projects")
        .select("id, name, code, client_id, contract_id")
        .eq("company_id", company.id)
        .order("name"),
      supabase
        .from("clients")
        .select("id, name")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("name"),
      supabase
        .from("contracts")
        .select("id, number, title, client_id")
        .eq("company_id", company.id)
        .order("number", { ascending: false }),
    ]).then(([profilesRes, projectsRes, clientsRes, contractsRes]) => {
      setEmployees(
        (profilesRes.data ?? []).map((p) => ({
          id: p.id,
          name: p.full_name ?? p.email,
        })),
      );
      setProjects((projectsRes.data ?? []) as any);
      setClients((clientsRes.data ?? []) as any);
      setContracts((contractsRes.data ?? []) as any);
    });
  }, [company?.id]);

  // Reset cascading filters
  useEffect(() => {
    if (clientFilter !== "all" && contractFilter !== "all") {
      const c = contracts.find((x) => x.id === contractFilter);
      if (c && c.client_id && c.client_id !== clientFilter) setContractFilter("all");
    }
    if (clientFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.client_id && p.client_id !== clientFilter) setProjectFilter("all");
    }
    if (contractFilter !== "all" && projectFilter !== "all") {
      const p = projects.find((x) => x.id === projectFilter);
      if (p && p.contract_id && p.contract_id !== contractFilter) setProjectFilter("all");
    }
  }, [clientFilter, contractFilter, projectFilter, contracts, projects]);

  const visibleContracts =
    clientFilter === "all" ? contracts : contracts.filter((c) => c.client_id === clientFilter);

  const filteredProjects = projects.filter((p) => {
    if (clientFilter !== "all" && p.client_id !== clientFilter) return false;
    if (contractFilter !== "all" && p.contract_id !== contractFilter) return false;
    return true;
  });

  const load = async () => {
    if (!periodStart || !periodEnd) return;
    setLoading(true);
    try {
      const r = await reportFn({
        data: {
          periodStart,
          periodEnd,
          userId: userFilter === "all" ? null : userFilter,
          projectId: projectFilter === "all" ? null : projectFilter,
          clientId: clientFilter === "all" ? null : clientFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
        },
      });
      setData(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodStart, periodEnd, userFilter, projectFilter, clientFilter, contractFilter]);

  const handleExportCsv = () => {
    if (!data) return;
    setExporting("csv");
    try {
      const headers = [
        t("pages.reports.payroll.csvHeaders.employee"),
        t("pages.reports.payroll.csvHeaders.email"),
        t("pages.reports.payroll.csvHeaders.approvedHours"),
        t("pages.reports.payroll.csvHeaders.pendingHours"),
        t("pages.reports.payroll.csvHeaders.rejectedHours"),
        t("pages.reports.payroll.csvHeaders.entries"),
        t("pages.reports.payroll.csvHeaders.hourlyRate"),
        t("pages.reports.payroll.csvHeaders.estimatedGross"),
      ];
      const rows = data.rows.map((r) => [
        r.userName,
        r.email ?? "",
        r.approvedHours,
        r.pendingHours,
        r.rejectedHours,
        r.entriesCount,
        r.hourlyRate,
        r.estimatedGross,
      ]);
      downloadCsv(
        `${t("pages.reports.payroll.csvFilenamePrefix")}-${periodStart}-${periodEnd}.csv`,
        headers,
        rows,
      );
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (!periodStart || !periodEnd) {
      toast.error(t("pages.reports.common.loadError"));
      return;
    }
    setExporting("pdf");
    try {
      const { pdfBase64, filename } = await pdfFn({
        data: {
          kind: "payroll",
          periodStart,
          periodEnd,
          userId: userFilter === "all" ? null : userFilter,
          projectId: projectFilter === "all" ? null : projectFilter,
          clientId: clientFilter === "all" ? null : clientFilter,
          contractId: contractFilter === "all" ? null : contractFilter,
        },
      });
      setPdfPreviewBase64(pdfBase64);
      setPdfPreviewFilename(filename);
      setPdfPreviewOpen(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("pages.reports.common.pdfError"));
    } finally {
      setExporting(null);
    }
  };

  const confirmDownloadPdf = () => {
    if (!pdfPreviewBase64) return;
    downloadPdfFromBase64(pdfPreviewFilename, pdfPreviewBase64);
    setPdfPreviewOpen(false);
  };

  const totals = data?.totals;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            {t("pages.reports.common.filters")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <div className="space-y-1.5">
              <Label htmlFor="period-start">{t("pages.reports.common.periodStart")}</Label>
              <Input
                id="period-start"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="period-end">{t("pages.reports.common.periodEnd")}</Label>
              <Input
                id="period-end"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.payroll.filters.employee")}</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.client", "Cliente")}</Label>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.contract", "Contrato")}</Label>
              <Select value={contractFilter} onValueChange={setContractFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {visibleContracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.number} – {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("pages.reports.financial.filters.project", "Obra")}</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.reports.common.all")}</SelectItem>
                  {filteredProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} – {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={load} disabled={loading} className="flex-1">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                {t("pages.reports.common.refresh")}
              </Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={!data || exporting !== null}
            >
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              {t("pages.reports.common.exportCsv")}
            </Button>
            <Button size="sm" onClick={handleExportPdf} disabled={!data || exporting !== null}>
              {exporting === "pdf" ? (
                <RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-1.5 h-4 w-4" />
              )}
              {t("pages.reports.common.exportPdf")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <KpiCard
              icon={<Users className="h-4 w-4" />}
              label={t("pages.reports.payroll.kpis.employees")}
              value={String(totals?.employeesCount ?? 0)}
            />
            <KpiCard
              icon={<Clock className="h-4 w-4" />}
              label={t("pages.reports.payroll.kpis.approvedHours")}
              value={`${formatNumber(totals?.approvedHours ?? 0, { maximumFractionDigits: 2 })}h`}
            />
            <KpiCard
              icon={<Clock className="h-4 w-4" />}
              label={t("pages.reports.payroll.kpis.pendingHours")}
              value={`${formatNumber(totals?.pendingHours ?? 0, { maximumFractionDigits: 2 })}h`}
              tone="warn"
            />
            <KpiCard
              icon={<Briefcase className="h-4 w-4" />}
              label={t("pages.reports.payroll.kpis.estimatedGross")}
              value={formatMoney(totals?.estimatedGross ?? 0, currency)}
              tone="success"
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t("pages.reports.payroll.byEmployee")}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  {t("pages.reports.payroll.noEntries")}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.reports.payroll.tableHeaders.employee")}</TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.approved")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.pending")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.rejected")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.hourlyRate")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.estimatedGross")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow key={r.userId}>
                        <TableCell>
                          <div className="font-medium">{r.userName}</div>
                          {r.email && (
                            <div className="text-xs text-muted-foreground">{r.email}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(r.approvedHours, { maximumFractionDigits: 2 })}h
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.pendingHours > 0 ? (
                            <Badge variant="outline" className="font-mono">
                              {formatNumber(r.pendingHours, { maximumFractionDigits: 2 })}h
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.rejectedHours > 0 ? (
                            <span className="text-destructive">
                              {formatNumber(r.rejectedHours, { maximumFractionDigits: 2 })}h
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(r.hourlyRate, currency)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatMoney(r.estimatedGross, currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {data.byProject.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t("pages.reports.payroll.byProject")}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("pages.reports.payroll.tableHeaders.project")}</TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.employees")}
                      </TableHead>
                      <TableHead className="text-right">
                        {t("pages.reports.payroll.tableHeaders.hours")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.byProject.map((p) => (
                      <TableRow key={p.projectId ?? "none"}>
                        <TableCell>{p.projectName}</TableCell>
                        <TableCell className="text-right tabular-nums">{p.employees}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatNumber(p.hours, { maximumFractionDigits: 2 })}h
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      ) : null}

      <PdfPreviewDialog
        open={pdfPreviewOpen}
        onOpenChange={setPdfPreviewOpen}
        pdfBase64={pdfPreviewBase64}
        filename={pdfPreviewFilename}
        onDownload={confirmDownloadPdf}
      />
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: "success" | "warn" | "danger";
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-600 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-600 dark:text-amber-400"
        : tone === "danger"
          ? "text-destructive"
          : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className={`mt-2 text-2xl font-bold tabular-nums ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
